import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { NotificationDTO, RestService } from 'cargo-chief-commons';
import { ComponentStore } from '@ngrx/component-store';
import { Injectable } from '@angular/core';
import { filter, Observable, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, take, tap } from 'rxjs/operators';
import { UrlService } from '@service/url/url.service';
import { State } from '@state/core.state';
import { ErrorActions } from '@state/error';
import { Actions, ofType } from '@ngrx/effects';
import { WebSocketActions } from '../../../services/web-socket/web-socket.actions';
import { selectSession } from '@state/session';

interface NotificationsState {
	loading: boolean;
	notifications: EntityState<NotificationDTO>;
	unread: number;
}

const adapter = createEntityAdapter<NotificationDTO>({
	selectId: (notification) => notification.id,
	sortComparer: (a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)),
});

const initialState: NotificationsState = {
	loading: true,
	notifications: adapter.getInitialState(),
	unread: 0,
};

const mappings = {
	price_lanes: 'RFP',
};

const { selectAll } = adapter.getSelectors();

@Injectable({ providedIn: 'root' })
export class NotificationsStore extends ComponentStore<NotificationsState> {
	public loading$ = this.select((state) => state.loading);
	public unread$ = this.select((state) => state.unread);
	public notifications$ = this.select(
		this.select((state) => state.notifications),
		selectAll
	);

	constructor(
		private readonly store: Store<State>,
		private readonly actions: Actions<any>,
		private readonly restService: RestService,
		private readonly urlService: UrlService
	) {
		super(initialState);

		let userId;
		this.store
			.select(selectSession)
			.pipe(take(1))
			.subscribe((session) => (userId = session.data.user.id));

		this.actions
			.pipe(
				ofType(WebSocketActions.read),
				filter((message: any) => message?.payload?.subject === 'notifications'),
				filter((message: any) => message?.payload?.notification.userId === userId),
				map((message: any) => message?.payload?.notification)
			)
			.subscribe((notification: NotificationDTO) => {
				if (!this.isLoading()) {
					this.upsertNotification(notification);
				}
			});

		this.notifications$.subscribe((notifications: NotificationDTO[]) => {
			this.updateUnread(notifications.filter((notification) => !notification.read).length);
		});
	}

	public readonly loadNotifications = this.effect((userId$: Observable<number>) => {
		return userId$.pipe(
			tap(() => {
				this.setLoading(true);
			}),
			switchMap((userId: number) =>
				this.restService.GET(this.urlService.getNotifications(userId)).pipe(
					map((response: any) => {
						response = response.map((notification: NotificationDTO) => ({
							...notification,
						}));
						return response;
					}),
					tap({
						next: (response: any) => this.resetNotifications(response),
						error: () =>
							this.store.dispatch(
								ErrorActions.error.show({ payload: { message: 'Failed to load notifications.' } })
							),
					})
				)
			),
			tap(() => this.setLoading(false))
		);
	});

	public readonly isLoading = (): boolean => {
		let loading;

		this.loading$.pipe(take(1)).subscribe((isLoading) => (loading = isLoading));

		return loading;
	};

	public readonly resetNotifications = this.updater((state, notifications: NotificationDTO[]) => ({
		...state,
		notifications: adapter.setAll(notifications, state.notifications),
	}));

	public readonly upsertNotification = this.updater((state, notification: NotificationDTO) => ({
		...state,
		notifications: adapter.upsertOne(notification, state.notifications),
	}));

	public readonly removeNotification = this.updater((state, notification: NotificationDTO) => ({
		...state,
		notifications: adapter.removeOne(notification.id, state.notifications),
	}));

	public readonly updateUnread = this.updater((state, amount: number) => ({
		...state,
		unread: amount,
	}));

	public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

	public readonly clear = this.updater(() => ({ ...initialState }));
}
